import type { SupportedLanguage, LanguageConfigItem } from './types';

/**
 * Single source of truth for all language configurations
 * This object contains all language metadata in one place to follow DRY principles
 */
export const LANGUAGE_CONFIG: Record<SupportedLanguage, LanguageConfigItem> = {
	EN: {
		code: 'EN',
		domLang: 'en',
		antdLocale: 'en_US',
		displayName: 'English',
		isRTL: false,
	},
	ES: {
		code: 'ES',
		domLang: 'es',
		antdLocale: 'es_ES',
		displayName: 'Spanish',
		isRTL: false,
	},
	PT: {
		code: 'PT',
		domLang: 'pt',
		antdLocale: 'pt_PT',
		displayName: 'Portuguese',
		isRTL: false,
	},
	FR: {
		code: 'FR',
		domLang: 'fr',
		antdLocale: 'fr_FR',
		displayName: 'French',
		isRTL: false,
	},
	DE: {
		code: 'DE',
		domLang: 'de',
		antdLocale: 'de_DE',
		displayName: 'German',
		isRTL: false,
	},
	AR: {
		code: 'AR',
		domLang: 'ar',
		antdLocale: 'ar_EG',
		displayName: 'Arabic',
		isRTL: true,
	},
	EL: {
		code: 'EL',
		domLang: 'el',
		antdLocale: 'el_GR',
		displayName: 'Greek',
		isRTL: false,
	},
	ZH: {
		code: 'ZH',
		domLang: 'zh',
		antdLocale: 'zh_CN',
		displayName: 'Chinese',
		isRTL: false,
	},
	HI: {
		code: 'HI',
		domLang: 'hi',
		antdLocale: 'hi_IN',
		displayName: 'Hindi',
		isRTL: false,
	},
	MR: {
		code: 'MR',
		domLang: 'mr',
		antdLocale: 'mr_IN',
		displayName: 'Marathi',
		isRTL: false,
	},
	TA: {
		code: 'TA',
		domLang: 'ta',
		antdLocale: 'ta_IN',
		displayName: 'Tamil',
		isRTL: false,
	},
	ID: {
		code: 'ID',
		domLang: 'id',
		antdLocale: 'id_ID',
		displayName: 'Indonesian',
		isRTL: false,
	},
	HE: {
		code: 'HE',
		domLang: 'he',
		antdLocale: 'he_IL',
		displayName: 'Hebrew',
		isRTL: true,
	},
	IT: {
		code: 'IT',
		domLang: 'it',
		antdLocale: 'it_IT',
		displayName: 'Italian',
		isRTL: false,
	},
	JA: {
		code: 'JA',
		domLang: 'ja',
		antdLocale: 'ja_JP',
		displayName: 'Japanese',
		isRTL: false,
	},
	KO: {
		code: 'KO',
		domLang: 'ko',
		antdLocale: 'ko_KR',
		displayName: 'Korean',
		isRTL: false,
	},
	MS: {
		code: 'MS',
		domLang: 'ms',
		antdLocale: 'ms_MY',
		displayName: 'Malay',
		isRTL: false,
	},
	PL: {
		code: 'PL',
		domLang: 'pl',
		antdLocale: 'pl_PL',
		displayName: 'Polish',
		isRTL: false,
	},
	TH: {
		code: 'TH',
		domLang: 'th',
		antdLocale: 'th_TH',
		displayName: 'Thai',
		isRTL: false,
	},
	TR: {
		code: 'TR',
		domLang: 'tr',
		antdLocale: 'tr_TR',
		displayName: 'Turkish',
		isRTL: false,
	},
	UK: {
		code: 'UK',
		domLang: 'uk',
		antdLocale: 'uk_UA',
		displayName: 'Ukrainian',
		isRTL: false,
	},
	VI: {
		code: 'VI',
		domLang: 'vi',
		antdLocale: 'vi_VN',
		displayName: 'Vietnamese',
		isRTL: false,
	},
	CS: {
		code: 'CS',
		domLang: 'cs',
		antdLocale: 'cs_CZ',
		displayName: 'Czech',
		isRTL: false,
	},
	DA: {
		code: 'DA',
		domLang: 'da',
		antdLocale: 'da_DK',
		displayName: 'Danish',
		isRTL: false,
	},
	NL: {
		code: 'NL',
		domLang: 'nl',
		antdLocale: 'nl_NL',
		displayName: 'Dutch',
		isRTL: false,
	},
	RO: {
		code: 'RO',
		domLang: 'ro',
		antdLocale: 'ro_RO',
		displayName: 'Romanian',
		isRTL: false,
	},
	SK: {
		code: 'SK',
		domLang: 'sk',
		antdLocale: 'sk_SK',
		displayName: 'Slovak',
		isRTL: false,
	},
	HU: {
		code: 'HU',
		domLang: 'hu',
		antdLocale: 'hu_HU',
		displayName: 'Hungarian',
		isRTL: false,
	},
	HR: {
		code: 'HR',
		domLang: 'hr',
		antdLocale: 'hr_HR',
		displayName: 'Croatian',
		isRTL: false,
	},
	FI: {
		code: 'FI',
		domLang: 'fi',
		antdLocale: 'fi_FI',
		displayName: 'Finnish',
		isRTL: false,
	},
	SR: {
		code: 'SR',
		domLang: 'sr',
		antdLocale: 'sr_RS',
		displayName: 'Serbian',
		isRTL: false,
	},
	SL: {
		code: 'SL',
		domLang: 'sl',
		antdLocale: 'sl_SI',
		displayName: 'Slovenian',
		isRTL: false,
	},
	SQ: {
		code: 'SQ',
		domLang: 'sq',
		antdLocale: 'sq_AL',
		displayName: 'Albanian',
		isRTL: false,
	},
	BS: {
		code: 'BS',
		domLang: 'bs',
		antdLocale: 'bs_BA',
		displayName: 'Bosnian',
		isRTL: false,
	},
	LV: {
		code: 'LV',
		domLang: 'lv',
		antdLocale: 'lv_LV',
		displayName: 'Latvian',
		isRTL: false,
	},
	MK: {
		code: 'MK',
		domLang: 'mk',
		antdLocale: 'mk_MK',
		displayName: 'Macedonian',
		isRTL: false,
	},
	ET: {
		code: 'ET',
		domLang: 'et',
		antdLocale: 'et_EE',
		displayName: 'Estonian',
		isRTL: false,
	},
	LU: {
		code: 'LU',
		domLang: 'lb',
		antdLocale: 'lb_LU',
		displayName: 'Luxembourgish',
		isRTL: false,
	},
	MT: {
		code: 'MT',
		domLang: 'mt',
		antdLocale: 'mt_MT',
		displayName: 'Maltese',
		isRTL: false,
	},
	NO: {
		code: 'NO',
		domLang: 'no',
		antdLocale: 'nb_NO',
		displayName: 'Norwegian',
		isRTL: false,
	},
} as const;

/**
 * Supported languages - derived from the single source of truth
 */
export const SUPPORTED_LANGUAGES: SupportedLanguage[] = Object.keys(LANGUAGE_CONFIG) as SupportedLanguage[];

/**
 * Default fallback language
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'EN';

/**
 * Right-to-left languages - derived from the configuration
 */
export const RTL_LANGUAGES: SupportedLanguage[] = (
	Object.entries(LANGUAGE_CONFIG) as [SupportedLanguage, LanguageConfigItem][]
)
	.filter(([, config]) => config.isRTL)
	.map(([code]) => code as SupportedLanguage);

/**
 * Language mapping for various use cases - all derived from the single source of truth
 */
export const LANGUAGE_MAP = {
	/**
	 * For DOM lang attribute (lowercase) - derived from configuration
	 */
	DOM_LANG: Object.fromEntries(
		(Object.entries(LANGUAGE_CONFIG) as [SupportedLanguage, LanguageConfigItem][]).map(([code, config]) => [
			code,
			config.domLang,
		])
	) as Record<SupportedLanguage, string>,

	/**
	 * For direction attribute - derived from configuration
	 */
	DIR: Object.fromEntries(
		(Object.entries(LANGUAGE_CONFIG) as [SupportedLanguage, LanguageConfigItem][]).map(([code, config]) => [
			code,
			config.isRTL ? 'rtl' : 'ltr',
		])
	) as Record<SupportedLanguage, 'ltr' | 'rtl'>,

	/**
	 * For Ant Design locales - derived from configuration
	 */
	ANTD_LOCALE_MAP: Object.fromEntries(
		(Object.entries(LANGUAGE_CONFIG) as [SupportedLanguage, LanguageConfigItem][]).map(([code, config]) => [
			code,
			config.antdLocale,
		])
	) as Record<SupportedLanguage, string>,

	/**
	 * Human-readable language names for display in UI - derived from configuration
	 */
	DISPLAY_NAMES: Object.fromEntries(
		(Object.entries(LANGUAGE_CONFIG) as [SupportedLanguage, LanguageConfigItem][]).map(([code, config]) => [
			code,
			config.displayName,
		])
	) as Record<SupportedLanguage, string>,
} as const;
